import { Component, Input, forwardRef } from '@angular/core';
import { BaseLookupEntity, ILookupDialogConfig, LookupDialogComponent, EntitySubTypes } from '../lookup-dialog.component';
import { DialogService, IDialogHandle } from '../../dialogs/dialog.service';
import { DialogCloseReason, IDialogOnClose } from '../../dialogs/base-dialog';
import { CrmEntityLookupKnownSubType, CrmEntityTypeEnum } from '../../crm-entities/crm-entities.model';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export const CD_LOOKUPINPUT_CONTROL_INPUT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LookupInputComponent),
  multi: true
};


export interface ILookupData {
  name: string;
  id: string;
};

export interface ILookupDataWithType extends ILookupData
{
  type: CrmEntityTypeEnum;
  additionalEntityInfo?: any;
}

@Component({
  selector: 'lookup-input',
  styleUrls: ['./lookup-input.scss'],
  templateUrl: './lookup-input.html',
  providers: [CD_LOOKUPINPUT_CONTROL_INPUT_VALUE_ACCESSOR]
})
export class LookupInputComponent implements ControlValueAccessor {

  chosenEntity: ILookupDataWithType;
  multipleChoosenEntities: ILookupDataWithType[] = [];
  lookupDialogHandle: IDialogHandle;

  @Input() crmEntityTypes: CrmEntityTypeEnum[];
  @Input() entitySubTypes?: EntitySubTypes;
  @Input() editorType: string;
  @Input() includeLookupViews = true; // include additional entity viewes by default
  @Input() singleSelection = true;
  @Input() disabled = false;

  // When this component is consumed with ngModel directive,
  // the ngModel directive will override this function (by calling the registerOnChange function)
  // and will update the ngModel "value" with the parameter passed to the function. ngModel attribute
  // must be present on the consuming component
  controlAccessorNotifyChange: (_?: any) => void = function () {
    // insert a noop function to make things simpler when not working with forms
    return;
  };

  controlAccessorNotifyTouched: () => void = function () {
    // insert a noop function to make things simpler when not working with forms
    return;
  };

  constructor(private dialogService: DialogService) {
    this.resetSelection();
  }

  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      if (this.singleSelection) {
        this.chosenEntity.name = value.name;
        this.chosenEntity.id = value.id;
      } else {
        if (value && Array.isArray(value) && value.length > 0) {
          this.multipleChoosenEntities = [];
          value.forEach((entity: ILookupDataWithType) => {
            this.multipleChoosenEntities.push({
              name: entity.name,
              id: entity.id,
              type: entity.type
            });
          });
        }
      }
    }
  }

  registerOnChange(fn: any): void {
    this.controlAccessorNotifyChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.controlAccessorNotifyTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public openLookupDialog(event: MouseEvent) {
    if (this.lookupDialogHandle) {
      // this prevents for the dialog to be opened twice while keeping the notification of the click mouse event
      // to the parent ourside the lookup-input (some consumers will want to register on that event)
      // we could have it being callled wtice since it's being triggered by the magnifying class in multy select view
      return;
    }

    this.controlAccessorNotifyTouched();

    this.lookupDialogHandle = this.dialogService.createDialog<LookupDialogComponent>(LookupDialogComponent, {
      entityTypes: this.crmEntityTypes,
      entitySubTypes: this.entitySubTypes,
      singleSelection: this.singleSelection,
      includeLookupViews: this.includeLookupViews,
      editorType: this.editorType
    } as ILookupDialogConfig);

    this.lookupDialogHandle.onClose.subscribe((result: IDialogOnClose) => {
      // forget the handle since the dialog is closing. also acts as indication if the dialog is currently open
      this.lookupDialogHandle = null;

      if (result.reason === DialogCloseReason.OK) {
        const selectedRecords = result.data.selected;
        if (selectedRecords == null || selectedRecords.length === 0) {
          return;
        }
        if (this.singleSelection) {
          this.chosenEntity = this.entityRecordFromDialogResult(selectedRecords[0], result.data.entityType);
          this.controlAccessorNotifyChange(this.chosenEntity);
        } else {
          this.multipleChoosenEntities = [];
          selectedRecords.forEach((selectedRecord: BaseLookupEntity) => {
            this.multipleChoosenEntities.push(this.entityRecordFromDialogResult(selectedRecord, result.data.entityType));
          });
          this.controlAccessorNotifyChange(this.multipleChoosenEntities);
        }
      }
    });
  }

  public onClearInputClicked(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();

    this.resetSelection();
    this.controlAccessorNotifyChange(null);
  }

  public onRemoveChoosenEntityClicked(choosenEntityToRemove: ILookupDataWithType, event: MouseEvent) {
    event.stopPropagation();

    this.multipleChoosenEntities = this.multipleChoosenEntities.filter(entity => entity.id !== choosenEntityToRemove.id);
    this.controlAccessorNotifyChange(this.multipleChoosenEntities);
  }

  public closeLookupDialog() {
    this.lookupDialogHandle.close();
  }

  public onMultiViewClicked(event: MouseEvent) {
    if (this.multipleChoosenEntities.length === 0) {
      this.openLookupDialog(event);
    }
  }

  private resetSelection() {
    this.chosenEntity = {
      name: '',
      id: '',
      type: CrmEntityTypeEnum.None
    };
  }

  private entityRecordFromDialogResult(dialogResultRecord: BaseLookupEntity, entityType: CrmEntityTypeEnum) {
    return {
      name: dialogResultRecord.Name || dialogResultRecord.CurrencyName,
      id: dialogResultRecord.Id,
      type: entityType,
      additionalEntityInfo: dialogResultRecord
    };
  }
}
